import type { ReactNode } from 'react';
import { memo } from 'react';

import classNames from 'classnames';

import styles from './Numeral.module.scss';
import { formatPercents } from './percents';

type NumeralFormat = 'plain' | 'compact' | 'monetary' | 'percents';

export type ChangeType = 'positive' | 'negative' | 'neutral';

const getChangeType = (value: number): ChangeType => {
  if (Number.isNaN(value) || value === 0) {
    return 'neutral';
  }

  return value > 0 ? 'positive' : 'negative';
};

type RenderProps = {
  renderValue: string;
  changeType: ChangeType;
};

export interface NumeralProps {
  value?: number;
  format?: NumeralFormat;
  highlightChanges?: boolean;
  showSign?: boolean;
  renderFunction?: (props: RenderProps) => ReactNode;
}

const MONETARY_NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  useGrouping: true,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatUnit = (value: number, divider: number, suffix: string): string => {
  const base = Math.floor((value / divider) * 10) / 10;

  return Number.isInteger(base) ? `${base.toFixed(0)}${suffix}` : `${base.toFixed(1)}${suffix}`;
};

const FORMATTERS_BY_FORMAT = {
  plain: (value: number) => value.toString(),
  compact: (value: number) => {
    if (value >= 1000000) {
      return formatUnit(value, 1000000, 'M');
    }

    if (value >= 1000) {
      return formatUnit(value, 1000, 'K');
    }

    return value.toString();
  },
  monetary: MONETARY_NUMBER_FORMATTER.format,
  percents: formatPercents,
} as const;

const CHANGE_TYPE_TO_SIGN_MAP: Record<ChangeType, string> = {
  positive: '+',
  negative: '-',
  neutral: '',
};

export const Numeral = memo(
  ({
    value,
    format = 'plain',
    renderFunction,
    highlightChanges = false,
    showSign = false,
  }: NumeralProps) => {
    const formattedValue = typeof value === 'number' ? FORMATTERS_BY_FORMAT[format](value) : '';
    const changeType = getChangeType(parseFloat(formattedValue));
    const sign = showSign ? CHANGE_TYPE_TO_SIGN_MAP[changeType] : '';
    const renderValue =
      typeof value === 'number' ? `${sign}${FORMATTERS_BY_FORMAT[format](value)}` : '';

    return (
      typeof value === 'number' && (
        <span className={classNames(styles['container'], highlightChanges && styles[changeType])}>
          {renderFunction ? renderFunction({ renderValue, changeType }) : renderValue}
        </span>
      )
    );
  },
);
